// Bank accounts of contract parties outside Saudi Arabia.
//
// The portal lets a business add a party with a non-Saudi phone number. Such a party enters any
// valid IBAN, the bank's country and name, and may attach a bank-info document. The backend saves
// it on the user's bank record (GET /api/users/{id}/banks) with `international: true`, `country`,
// `bankName` and `document`, against the "NAN" (unsupported) bank. SingleView can't pay these
// accounts and the backend doesn't block it, so the admin offers manual bank transfer only.
import { getDownloadPresignedUrl } from "../api/services/file";

const DOCUMENT_URL_EXPIRATION = 60 * 60; // 1 hour — only needed long enough to open it.

export interface IBankDocument {
  bucketName?: string;
  objectName?: string;
  fileName?: string;
  /** Present when the backend already resolved a readable URL. */
  url?: string;
}

/** The international-only fields a bank record may carry next to the Saudi ones. */
export interface IInternationalBankFields {
  international?: boolean;
  /** ISO 3166-1 alpha-2 of the bank's country, e.g. "EG". */
  country?: string;
  /** Free-text bank name; international accounts carry the placeholder "NAN" `bank`. */
  bankName?: string;
  /** SWIFT / BIC of the receiving bank (top-level; `bank.swiftCode` is the "NAN" bank's). */
  swiftCode?: string;
  branchName?: string;
  /** Account holder's address, often required by the sending bank for a SWIFT transfer. */
  beneficiaryAddress?: string;
  document?: IBankDocument | null;
}

/** Structural so both BankResponseData and IWithdrawDetailsBank fit. */
type AnyBank =
  | (IInternationalBankFields & {
      bank?: { arabicName?: string } | null;
      selectedBank?: { arabicName?: string } | null;
    })
  | null
  | undefined;

/** The backend's flag — `bank` is never null (it's the "NAN" bank), so don't infer from it. */
export const isInternationalBank = (bank: AnyBank): boolean =>
  bank?.international === true;

/** A party is international when its phone is outside KSA (legacy numbers have no "+"). */
export const isInternationalPhoneNumber = (phoneNumber?: string | number) => {
  const raw = `${phoneNumber ?? ""}`.replace(/\s/g, "");
  return raw.startsWith("+") && !raw.startsWith("+966");
};

/** The account's SWIFT lives top-level; `bank.swiftCode` is only a fallback for older records. */
export const getBankSwiftCode = (
  bank:
    | { swiftCode?: string | null; bank?: { swiftCode?: string | null } | null }
    | null
    | undefined
): string => {
  const swiftCode = bank?.swiftCode || bank?.bank?.swiftCode || "";
  return swiftCode.toLowerCase() === "nan" ? "" : swiftCode;
};

/** The entered name for international banks (their `bank` is "NAN"), the SAMA name otherwise. */
export const getBankDisplayName = (bank: AnyBank): string =>
  (isInternationalBank(bank) && bank?.bankName) ||
  bank?.bank?.arabicName ||
  bank?.selectedBank?.arabicName ||
  "";

/** "EG" → "مصر". Falls back to the raw code if the runtime can't name it. */
export const getCountryDisplayName = (isoCode?: string): string => {
  if (!isoCode) return "";
  try {
    return (
      new Intl.DisplayNames(["ar"], { type: "region" }).of(
        isoCode.toUpperCase()
      ) || isoCode
    );
  } catch {
    return isoCode;
  }
};

export const getBankDocumentName = (document?: IBankDocument | null) =>
  document?.fileName || document?.objectName?.split("/").pop() || "";

/**
 * Opens the party's bank document in a new tab. The portal uploads it privately, so a presigned
 * GET is minted on demand. The tab is opened synchronously first so the popup blocker treats it
 * as part of the click, then pointed at the URL once it resolves.
 */
export const openBankDocument = async (
  document?: IBankDocument | null
): Promise<void> => {
  if (!document) return;
  const tab = window.open("", "_blank");
  if (tab) tab.opener = null;
  try {
    const url =
      document.url ||
      (document.bucketName && document.objectName
        ? await getDownloadPresignedUrl({
            bucket_name: document.bucketName,
            object_name: document.objectName,
            expiration: DOCUMENT_URL_EXPIRATION,
          })
        : "");
    if (!url) throw new Error("Bank document has no location");
    if (tab) tab.location.href = url;
    else window.open(url, "_blank", "noopener,noreferrer");
  } catch (error) {
    tab?.close();
    throw error;
  }
};

type ProfileAddress = {
  street?: string;
  district?: string;
  city?: string;
  postalCode?: string;
  countryCode?: string;
  nationalAddress?: string;
  defaultAddress?: boolean;
};

/**
 * One line for a profile address — the fallback for the account holder address when the bank
 * record has no `beneficiaryAddress`. Uses the default address, else the first one.
 */
export const formatProfileAddress = (
  addresses?: ProfileAddress[] | null
): string => {
  const address =
    addresses?.find((item) => item?.defaultAddress) ?? addresses?.[0];
  if (!address) return "";
  return [
    address.street,
    address.district,
    address.city,
    address.postalCode,
    address.countryCode ? getCountryDisplayName(address.countryCode) : "",
    address.nationalAddress,
  ]
    .map((part) => `${part ?? ""}`.trim())
    .filter(Boolean)
    .join("، ");
};
